import React, { useState, useEffect, useContext } from "react";
import { IconButton, Tooltip, Typography, Box } from '@mui/material';
import { ArrowClockwise, Check } from '@phosphor-icons/react';
import { ThemeContext } from 'styled-components';

/**
 * Generic wrapper component for any input with action buttons
 * Supports refresh and success actions with consistent spacing
 */
const InputWithActions = ({
    // Action button props
    showRefreshButton = false,
    onRefresh,
    isRefreshing = false,
    refreshTooltip = "Refresh",
    refreshIcon = ArrowClockwise,
    fullWidth = true,

    showSuccessButton = false,
    successTooltip = "Success",
    successIcon = Check,
    successColor,

    // Right-side action button props
    showActionButton = false,
    onActionClick,
    actionIcon,
    actionTooltip = "",
    actionButtonDisabled = false,
    actionIconSize = 24,
    actionIconColor,

    // Cooldown props
    cooldownDuration = 60, // seconds
    cooldownWarningDuration = 5, // seconds

    // Layout props
    buttonWidth = '25px',
    gap = '8px',
    inputHeight = '40px',
    inputType = 'code', // 'otp' or 'textfield'
    forceSpacing = false, // Force spacing even when no buttons are shown

    // Children (the actual input component)
    children
}) => {
    const theme = useContext(ThemeContext);
    const [cooldownEndTime, setCooldownEndTime] = useState(null);
    const [showCooldownWarning, setShowCooldownWarning] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(0);

    // Initialize cooldown when component mounts
    useEffect(() => {
        if (showRefreshButton) {
            const initialCooldown = Date.now() + (cooldownDuration * 1000);
            setCooldownEndTime(initialCooldown);
            setTimeRemaining(cooldownDuration);
        }
    }, [showRefreshButton, cooldownDuration]);

    // Timer to update remaining time
    useEffect(() => {
        if (!cooldownEndTime || !showRefreshButton) return;

        const timer = setInterval(() => {
            const now = Date.now();
            const remaining = Math.max(0, Math.ceil((cooldownEndTime - now) / 1000));
            setTimeRemaining(remaining);

            if (remaining === 0) {
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownEndTime, showRefreshButton]);

    const isCooldownActive = timeRemaining > 0;

    const handleRefreshClick = async () => {
        if (isCooldownActive) {
            setShowCooldownWarning(true);
            // Hide warning after specified duration
            setTimeout(() => setShowCooldownWarning(false), cooldownWarningDuration * 1000);
            return;
        }

        if (onRefresh) {
            await onRefresh();
            // Start new cooldown after successful refresh
            const newCooldown = Date.now() + (cooldownDuration * 1000);
            setCooldownEndTime(newCooldown);
            setTimeRemaining(cooldownDuration);
        }
    };

    // Calculate if we need action buttons
    const hasActions = showRefreshButton || showSuccessButton || forceSpacing || showActionButton;
    const actualButtonWidth = hasActions ? buttonWidth : '0px';
    const actualGap = hasActions ? gap : '0px';

    // Calculate vertical alignment based on input type
    const buttonAlignment = inputType === 'text' ? 'flex-start' : 'center';
    const buttonMarginTop = inputType === 'text' ? '8px' : '0px';

    return (
        <div style={{ display: 'flex', alignItems: buttonAlignment, width: '100%', gap: actualGap }}>
            {/* Left Action Button Container */}
            {hasActions && (
                <div style={{
                    width: actualButtonWidth,
                    height: inputHeight,
                    marginTop: buttonMarginTop,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    {/* Refresh Button */}
                    {showRefreshButton && (
                        <Tooltip title={
                            isRefreshing
                                ? "Refreshing..."
                                : isCooldownActive
                                    ? `Please wait ${timeRemaining}s before refreshing`
                                    : refreshTooltip
                        }>
                            <span>
                                <IconButton
                                    onClick={handleRefreshClick}
                                    disabled={isRefreshing || isCooldownActive}
                                    sx={{
                                        color: isCooldownActive ? theme.colors.overlayTextDisabled : 'inherit',
                                        '&:hover': {
                                            backgroundColor: isCooldownActive ? 'transparent' : theme.colors.overlayHover,
                                        },
                                        '&:disabled': {
                                            color: theme.colors.overlayTextDisabled,
                                        }
                                    }}
                                >
                                    {React.createElement(refreshIcon, {
                                        size: 24,
                                        weight: "bold",
                                        color: theme.colors.brandWarning,
                                        style: {
                                            animation: isRefreshing ? 'spin 1s linear infinite' : 'none'
                                        }
                                    })}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}

                    {/* Success Button */}
                    {showSuccessButton && (
                        <Tooltip title={successTooltip} arrow>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: actualButtonWidth,
                                    height: inputHeight
                                }}
                            >
                                {React.createElement(successIcon, {
                                    size: 24,
                                    color: successColor || theme.colors.brandSuccess,
                                    weight: "bold"
                                })}
                            </Box>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Input Container */}
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    flex: 1,
                    position: 'relative'
                }}
            >
                {children}
            </div>

            {/* Right Action Button Container */}
            {hasActions && (
                <div style={{
                    width: actualButtonWidth,
                    height: inputHeight,
                    marginTop: buttonMarginTop,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    {/* Right-side Action Button */}
                    {showActionButton && actionIcon && (
                        <Tooltip title={actionTooltip} arrow>
                            <span>
                                <IconButton
                                    onClick={onActionClick}
                                    disabled={actionButtonDisabled}
                                    sx={{
                                        color: 'inherit',
                                        '&:hover': {
                                            backgroundColor: theme.colors.overlayHover,
                                        },
                                        '&:disabled': {
                                            color: theme.colors.overlayDisabled,
                                        }
                                    }}
                                >
                                    {React.createElement(actionIcon, {
                                        size: actionIconSize,
                                        color: actionIconColor,
                                        weight: "bold"
                                    })}
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </div>
            )}

            {/* Cooldown Warning */}
            {showCooldownWarning && (
                <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: '0',
                    right: '0',
                    marginTop: '8px',
                    padding: '8px 12px',
                    backgroundColor: theme.colors.brandWarningLight,
                    color: theme.colors.text,
                    borderRadius: '4px',
                    fontSize: '16px', // Minimum 16px to prevent iOS zoom
                    textAlign: 'center',
                    zIndex: 1000,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                }}>
                    Please wait {timeRemaining} seconds before refreshing.
                </div>
            )}

            {/* CSS for spin animation */}
            <style>
                {`
                    @keyframes spin {
                        from { transform: rotate(0deg); }
                        to { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default InputWithActions;
